import {
  Button,
  Divider,
  FormControl,
  FormHelperText,
  FormLabel,
  Modal,
  ModalClose,
  ModalDialog,
  Stack,
  Textarea,
  ToggleButtonGroup,
  Typography,
} from '@healthinal/ui';
import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import {
  getGetTracingContactQueryKey,
  useSetTracingContactAssessmentResult,
} from '../../../../api/generated/contact-tracing-controller/contact-tracing-controller.ts';
import { SetTracingContactAssessmentResultDtoResult } from '../../../../api/generated/model';
import { queryClient } from '../../../../api/queryClient.ts';
import { FormActionButtons } from '../../../../components/form/FormActionButtons.tsx';
import { getTracingContactAssessmentResultName } from '../../../../utils/contactTracing.ts';
import { getStringOrUndefined } from '../../../../utils/string.ts';

interface FormValues {
  result: SetTracingContactAssessmentResultDtoResult | null;
  notes: string;
}

export function SetTracingAssessmentResultDialog({
  patientId,
  open,
  onClose,
  initialResult,
  initialNotes,
}: {
  patientId: string;
  open: boolean;
  onClose: () => void;
  initialResult?: SetTracingContactAssessmentResultDtoResult | null;
  initialNotes?: string | null;
}) {
  const {
    register,
    control,
    reset,
    handleSubmit,
    formState: { errors, isDirty: isFormDirty },
  } = useForm<FormValues>();

  const setResultMutation = useSetTracingContactAssessmentResult({
    mutation: {
      onSuccess: () => {
        void queryClient.invalidateQueries({ queryKey: getGetTracingContactQueryKey(patientId) });
        handleOnClose();
      },
      onError: () => toast.error('Beim Setzen des Resultats ist ein Fehler aufgetreten'),
    },
  });

  useEffect(() => {
    if (open) {
      reset({
        result: initialResult ?? null,
        notes: initialNotes ?? '',
      });
    }
  }, [open]);

  const handleOnClose = () => {
    reset();
    onClose();
  };

  return (
    <Modal open={open} onClose={handleOnClose}>
      <ModalDialog sx={{ width: 700 }}>
        <ModalClose />
        <Typography level={'h4'}>Abschluss</Typography>
        <Divider />
        <form
          onSubmit={handleSubmit((data) =>
            setResultMutation.mutate({
              id: patientId,
              data: {
                result: data.result!,
                notes: getStringOrUndefined(data.notes),
              },
            }),
          )}>
          <Stack gap={1}>
            <FormControl error={!!errors.result}>
              <FormLabel>
                <Typography>Resultat *</Typography>
              </FormLabel>
              <Controller
                name={`result`}
                control={control}
                rules={{ required: true }}
                render={({ field: { onChange, value } }) => (
                  <ToggleButtonGroup value={value} sx={{ flexWrap: 'wrap', gap: 1 }}>
                    {Object.values(SetTracingContactAssessmentResultDtoResult).map((result) => (
                      <Button value={result} key={result} onClick={() => onChange(result)}>
                        {getTracingContactAssessmentResultName(result)}
                      </Button>
                    ))}
                  </ToggleButtonGroup>
                )}
              />
              {errors.result && <FormHelperText>Resultat muss angegeben werden</FormHelperText>}
            </FormControl>
            <FormControl>
              <FormLabel>
                <Typography>Bemerkungen</Typography>
              </FormLabel>
              <Textarea {...register('notes')} minRows={2} maxRows={4} />
            </FormControl>
            <FormActionButtons
              onAbortClick={handleOnClose}
              disableSubmit={!isFormDirty}
              isLoading={setResultMutation.isPending}
            />
          </Stack>
        </form>
      </ModalDialog>
    </Modal>
  );
}
